import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {Person} from '../person/person';
import {PersonHeader} from '../person/person-header';
import {PersonHeaderSearchCriteria} from '../person/person-header-search-criteria';
import {PersonRole} from '../person/person-role';
import {Role} from '../person/role';
import {Entity} from '../entity/entity';
import {GoogleService} from './google-service';
import {RatingScheduleColumn} from '../model/rating-schedule-column';

@Service()
export class PersonService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);


  private urlBase = environment.serviceUrl;

  public search(criteria: PersonHeaderSearchCriteria): Observable<PersonHeader[]> {
    let url = this.urlBase + 'person/search';
    let body = JSON.stringify(criteria);
    return this.http.post<PersonHeader[]>(url, body, {headers: this.getPostHeaders()});
  }

  public getPersonById(personId: string): Observable<Person> {
    let url = this.urlBase + 'person/' + personId;
    return this.http.get<Person>(url, {headers: this.getRequestHeaders()});
  }

  public getContactPerson(personId: string): Observable<Person> {
    let url = this.urlBase + 'person/contact/' + personId;
    return this.http.get<Person>(url, {headers: this.getRequestHeaders()});
  }

  public save(person: Person): Observable<Person> {
    let url = this.urlBase + 'person/save';
    let body = JSON.stringify(person);
    return this.http.post<Person>(url, body, {headers: this.getPostHeaders()});
  }

  public saveSkyportContact(person: Person, entity: Entity): Observable<Person> {
    let url = this.urlBase + 'person/saveSkyportContact/' + entity.id.entityName + '/'
      + entity.id.entityId + '/' + entity.id.entityRevision;
    let body = JSON.stringify(person);
    return this.http.post<Person>(url, body, {headers: this.getPostHeaders()});
  }

  public getSkyportContact(entity: Entity): Observable<Person> {
    let url = this.urlBase + 'person/skyportContact/' + entity.id.entityName + '/'
      + entity.id.entityId + '/' + entity.id.entityRevision;
    return this.http.get<Person>(url, {headers: this.getRequestHeaders()});
  }

  public getAssignedRoles(personId: string): Observable<Role[]> {
    let url = this.urlBase + 'person/personRoles/' + personId;
    return this.http.get<Role[]>(url, {headers: this.getRequestHeaders()});
  }

  public getAvailableRoles(personId: string): Observable<Role[]> {
    let url = this.urlBase + 'person/availableRoles/' + personId;
    return this.http.get<Role[]>(url, {headers: this.getRequestHeaders()});
  }

  public addUserRole(personRole: PersonRole): Observable<string> {
    let url = this.urlBase + 'person/addRole';
    let body = JSON.stringify(personRole);
    return this.http.post(url, body, {headers: this.getPostHeaders(), responseType: 'text'});
  }

  public removeUserRole(personRole: PersonRole): Observable<string> {
    let url = this.urlBase + 'person/removeRole';
    let body = JSON.stringify(personRole);
    return this.http.post(url, body, {headers: this.getPostHeaders(), responseType: 'text'});
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  private getPostHeaders(contentType: string = 'application/json'): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", contentType);
  }

  public getByUserLogin(userLogin: string): Observable<Person> {
    let url = this.urlBase + 'person/userLogin';
    return this.http.post<Person>(url, userLogin, {headers: this.getPostHeaders('text/plain')});
  }

  getRatingScheduleColumns(): Observable<RatingScheduleColumn[]> {
    const url = this.urlBase + 'person/ratingScheduleColumns';
    return this.http.get<RatingScheduleColumn[]>(url, {headers: this.getRequestHeaders()});
  }

  saveRatingScheduleColumns(columnNames: string[]): Observable<string> {
    const url = this.urlBase + 'person/ratingScheduleColumns';
    return this.http.post(url, JSON.stringify(columnNames), {headers: this.getPostHeaders(), responseType: 'text'});
  }

}
